import {
  Alert,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import type { GameConfigField } from '../../api/gameConfig';

interface Props {
  field: GameConfigField;
  value: unknown;
  saves?: string[];
  onChange: (v: unknown) => void;
  disabled?: boolean;
}

export function ConfigField({ field, value, saves, onChange, disabled }: Props) {
  if (field.type === 'boolean') {
    return (
      <>
        <Tooltip title={field.description ?? ''} placement="right">
          <FormControlLabel
            control={
              <Switch
                checked={value === true}
                onChange={(e) => onChange(e.target.checked)}
                disabled={disabled}
                size="small"
              />
            }
            label={field.label}
          />
        </Tooltip>
        {field.key === 'StartNewGame' && value === true && (
          <Alert severity="error" sx={{ mt: 1 }} icon={<WarningAmberIcon />}>
            World will be wiped on next restart. After the world is created for the
            first time, set this back to false and save — otherwise every restart
            destroys the save.
          </Alert>
        )}
      </>
    );
  }

  if (field.type === 'enum' || (field.key === 'saveDirectory' && saves && saves.length > 0)) {
    const opts = field.key === 'saveDirectory' && saves?.length
      ? saves
      : (field.options ?? []);
    return (
      <FormControl size="small" fullWidth>
        <InputLabel>{field.label}</InputLabel>
        <Select
          label={field.label}
          value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        >
          {opts.map((o) => (
            <MenuItem key={o} value={o}>{o}</MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  if (field.type === 'number') {
    return (
      <Tooltip title={field.description ?? ''} placement="right">
        <TextField
          label={field.label}
          type="number"
          size="small"
          fullWidth
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
          disabled={disabled}
          inputProps={{ min: field.min, max: field.max, step: 1 }}
          helperText={field.min !== undefined && field.max !== undefined ? `${field.min}–${field.max}` : undefined}
        />
      </Tooltip>
    );
  }

  // string / steamid-list (steamid-list shown as plain text in config dialog)
  return (
    <Tooltip title={field.description ?? ''} placement="right">
      <TextField
        label={field.label}
        type="text"
        size="small"
        fullWidth
        value={String(value ?? '')}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        inputProps={{ maxLength: 512 }}
      />
    </Tooltip>
  );
}
